/** global cognigyEmbed */

import { createLogger } from 'helpers/logger';
import { getChatApiKey, getCognigyConfigUrl } from 'helpers/env';
import { postChatSession } from '../_services/session.service';
import {
  CASH_COGNIGY_URL_TOKEN,
  ONE_BUREAU_COGNIGY_URL_TOKEN,
  THREE_BUREAU_COGNIGY_URL_TOKEN,
} from 'types/cognigy.constants';

const logger = createLogger({
  name: 'cognigychat',
});

export const checkCognigyEmbedLoaded = () => {
  return window?.initWebchat;
};

export const isCognigyLoaded = () => {
  return window?.webchat;
};

const getCognigyConfigUrlToken = () => {
  return window?.webchat?.store?.getState()?.config?.URLToken;
};

export const isCognigyChatOpened = () => {
  const ui = window?.webchat?.store?.getState()?.ui;
  return ui?.open;
};

export const openCognigyChat = () => {
  const interval = setInterval(() => {
    if (!isCognigyChatOpened()) {
      window.webchat.open();
    } else {
      clearInterval(interval);
    }
  }, 1000);
};

export const closeCognigyChat = () => {
  return window.webchat.close();
};

export const removeCognigyChat = () => {
  const webChatRoot = document.querySelector('.webchat-root');
  if (webChatRoot) {
    webChatRoot.remove();
  }
  window.webchat = undefined;
};

export const initCognigyChat = props => {
  const { cognigyUrlType } = props ?? {};
  const configUrl = getCognigyConfigUrl(cognigyUrlType);
  const cognigySessionId = props?.cognigySessionId;
  const firstName = props?.firstName;
  if (checkCognigyEmbedLoaded()) {
    window
      .initWebchat(configUrl, {
        settings: {
          startBehavior: 'button',
          getStartedData: {
            cognigySessionId: cognigySessionId,
            firstName: firstName,
          },
          colorScheme: '#1d6fe0',
          disableBranding: true,
        },
      })
      .then(webchat => {
        window.webchat = webchat;
        webchat
          .connect()
          .then(() => {
            openCognigyChat();
          })
          .catch(() => {
            logger.error('Error connecting Cognigy chat.');
            throw new Error('Error connecting Cognigy chat.');
          });
      })
      .catch(e => {
        logger.error('Error initializing Cognigy chat.');
        throw new Error('Error initializing Cognigy chat.');
      });
  } else {
    logger.error('Cognigy Chat support is not enabled.');
    throw new Error('Cognigy Chat support is not enabled.');
  }
};

export const startCognigyChat = props => {
  if (
    getCognigyConfigUrlToken() === ONE_BUREAU_COGNIGY_URL_TOKEN ||
    getCognigyConfigUrlToken() === THREE_BUREAU_COGNIGY_URL_TOKEN
  ) {
    removeCognigyChat();
  }
  const body = { apiKey: getChatApiKey() };
  return new Promise((resolve, reject) => {
    postChatSession(body)
      .then(res => {
        const chatSessionProps = {
          cognigySessionId: res?.sessionId,
          firstName: props?.userInfo?.firstName,
          cognigyUrlType: props?.cognigyUrlType,
        };
        openChatSession(chatSessionProps);
      })
      .catch(err => reject(err));
  });
};

const openChatSession = props => {
  return new Promise((resolve, reject) => {
    if (isCognigyLoaded() && typeof window.webchat.open === 'function') {
      openCognigyChat();
      resolve();
    } else {
      try {
        initCognigyChat(props);
        resolve();
      } catch (error) {
        reject(error);
      }
    }
  });
};

export const startPlusCognigyChat = props => {
  if (getCognigyConfigUrlToken() === CASH_COGNIGY_URL_TOKEN) {
    removeCognigyChat();
  }

  return new Promise((resolve, reject) => {
    if (isCognigyLoaded() && typeof window.webchat.open === 'function') {
      openCognigyChat();
      resolve();
    } else {
      try {
        initPlusCognigyChat(props);
        resolve();
      } catch (error) {
        reject(error);
      }
    }
  });
};

const initPlusCognigyChat = props => {
  const { cognigyUrlType } = props ?? {};
  const configUrl = getCognigyConfigUrl(cognigyUrlType);
  if (checkCognigyEmbedLoaded()) {
    window
      .initWebchat(configUrl, {
        settings: {
          startBehavior: 'button',
          disableBranding: true,
        },
      })
      .then(webchat => {
        window.webchat = webchat;
        webchat
          .connect()
          .then(() => {
            openCognigyChat();
          })
          .catch(() => {
            logger.error('Error connecting Cognigy chat.');
            throw new Error('Error connecting Cognigy chat.');
          });
      })
      .catch(e => {
        logger.error('Error initializing Cognigy chat.');
        throw new Error('Error initializing Cognigy chat.');
      });
  } else {
    logger.error('Cognigy Chat support is not enabled.');
    throw new Error('Cognigy Chat support is not enabled.');
  }
};
